/**
 * Patch the document `<head>` so the browser favicon, iOS touch icon,
 * PWA manifest link, and page title all follow the current tenant's
 * branding.
 *
 * Coverage per platform:
 *   - iOS Safari "Add to Home Screen" — reads `<link rel="apple-touch-icon">`
 *     and `<title>` (or manifest short_name where iOS 16.4+ supports it).
 *     Our runtime DOM patch of both is sufficient.
 *   - Chrome Android "Add to Home Screen" / "Install" — requires a real
 *     `<link rel="manifest">` pointing at a manifest.webmanifest with
 *     `name`, `icons` and `start_url`. Without a manifest, Chrome falls
 *     back to a letter-tile icon and the HTML `<title>` snapshot, which
 *     is why the initial runtime-only fix produced an "S" letter tile
 *     and the "SupplyScanner" label instead of the tenant logo/name.
 *
 * The manifest itself is served per-tenant by the backend at
 * `/api/tenants/<slug>/manifest.webmanifest` — see
 * `backend/tenants/views.py::tenant_manifest`. That endpoint reads the
 * tenant's uploaded logo and inlines a fresh S3 presigned URL for the
 * icon sources.
 *
 * When the tenant has no uploaded logo we fall back to `/vite.svg` for
 * both the favicon and the manifest icon — the same default the app
 * shipped with before branding work landed.
 *
 * Called from `ProtectedRoute` alongside `applyBrandColors` so it runs
 * on every authenticated route regardless of role permissions.
 */

const DEFAULT_ICON = '/vite.svg'
const DEFAULT_TITLE = 'SupplyScanner'
// Absolute base URL for the backend. On dev this is
// `https://api.dev.supply-scanner.com`; on prod / staging similar. The
// frontend uses this for every `/api/*` call because the SPA and the
// API are served from different subdomains. Falls back to empty string
// (same-origin) for local development where they're both on
// http://localhost.
const API_BASE = import.meta.env.VITE_API_URL || ''

interface AppIconInput {
  /** Presigned S3 URL for the tenant's uploaded logo, or null. */
  logoUrl: string | null | undefined
  /** Tenant display name — used as the document title (home-screen label). */
  tenantName: string | null | undefined
  /**
   * Tenant slug — used to build the manifest endpoint URL. When absent
   * the manifest link is left alone (no per-tenant manifest is injected)
   * so the app still behaves as a plain SPA on unauthenticated routes.
   */
  tenantSlug: string | null | undefined
}

/**
 * Ensure a `<link>` with the given `rel` exists in `<head>` and return
 * it. Creates one lazily when index.html didn't ship it.
 */
function ensureLink(rel: string): HTMLLinkElement {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.rel = rel
    document.head.appendChild(el)
  }
  return el
}

export function applyAppIcon({ logoUrl, tenantName, tenantSlug }: AppIconInput) {
  const iconHref = logoUrl || DEFAULT_ICON
  const title = tenantName || DEFAULT_TITLE

  // Browser favicon. Bitmaps drop the `type` hint so the browser sniffs
  // from the response; SVG default keeps `type` set explicitly.
  const favicon = ensureLink('icon')
  favicon.href = iconHref
  if (logoUrl) {
    favicon.removeAttribute('type')
  } else {
    favicon.type = 'image/svg+xml'
  }

  // iOS Safari "Add to Home Screen" shortcut icon.
  const appleIcon = ensureLink('apple-touch-icon')
  appleIcon.href = iconHref

  // Chrome Android PWA manifest — the piece that was missing and left
  // the letter-tile / "SupplyScanner" fallback surfacing on shortcut
  // install. Only attached when we have a tenant slug; the manifest
  // endpoint is scoped per slug and can't be resolved otherwise.
  //
  // Uses the absolute API base URL (`VITE_API_URL`) because the frontend
  // (app.dev.supply-scanner.com) and backend (api.dev.supply-scanner.com)
  // live on different subdomains — a relative `/api/…` would resolve to
  // the SPA host and get the index.html fallback from nginx instead of
  // hitting Django. `crossorigin="anonymous"` is required by Chrome for
  // cross-origin manifests and matches the backend CORS config (public
  // endpoint, no cookies needed).
  if (tenantSlug) {
    const manifest = ensureLink('manifest')
    const nextHref = `${API_BASE}/api/tenants/${tenantSlug}/manifest.webmanifest`
    // Avoid touching the href when unchanged so Chrome doesn't retrigger
    // manifest fetching on every render.
    if (manifest.getAttribute('href') !== nextHref) {
      manifest.setAttribute('href', nextHref)
    }
    if (manifest.getAttribute('crossorigin') !== 'anonymous') {
      manifest.setAttribute('crossorigin', 'anonymous')
    }
  }

  // Home-screen label + browser tab title.
  if (document.title !== title) {
    document.title = title
  }
}
